import { ActionButton, Button, Text } from '@react-spectrum/s2';
import Download from '@react-spectrum/s2/icons/Download';
import Upload from '@react-spectrum/s2/icons/Upload';
import { useCallback, useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi';
import { ImageEditingOverlay } from '@/components/artifact/ImageEditingOverlay';
import {
	TYPE_TITLES,
	isArtifactActionable,
} from '@/components/artifact/panelMeta';
import { RailTooltip } from '@/components/artifact/RailTooltip';
import { toolRailButtonClass } from '@/components/artifact/toolRail';
import { useEnsureSetupSnapshot } from '@/components/artifact/useEnsureSetupSnapshot';
import { useGoToNextStep } from '@/components/artifact/useGoToNextStep';
import { useImageReplace } from '@/components/artifact/useImageReplace';
import { VibeEditButton } from '@/components/artifact/VibeEditButton';
import { BlobImage } from '@/components/blob/BlobImage';
import { BridgewayGlyph } from '@/components/BridgewayGlyph';
import { FireflyIcon } from '@/components/icons/FireflyIcon';
import { useFireflyEditModal } from '@/features/fireflyImageEditor';
import { useIsGeneratingImages } from '@/hooks/useIsGeneratingImages';
import { ChatPanelLayout } from '@/spaces/chat/panels/ChatPanelLayout';
import { SuccessInterstitial } from '@/spaces/chat/panels/SuccessInterstitial';
import { useDownloadAd } from '@/spaces/content/useDownloadAd';
import { cn } from '@/utils/cn';
import { trackEvent } from '@/utils/fullstory';

import type { DigitalAd } from '../../../../../bridge/types/artifact.ts';

interface DigitalAdArtifactPanelProps {
	artifact: DigitalAd;
	onClose: () => void;
	style?: React.CSSProperties;
}

export const DigitalAdArtifactPanel = ({
	artifact,
	onClose,
	style,
}: DigitalAdArtifactPanelProps) => {
	const bridgeApi = useBridgeApi();
	const goToNextItem = useGoToNextStep();
	useEnsureSetupSnapshot(artifact.process_id);
	const [savedInterstitialOpen, setSavedInterstitialOpen] = useState(false);
	const [isViewportHovered, setIsViewportHovered] = useState(false);
	const { download: handleDownload } = useDownloadAd(artifact);
	const fireflyEdit = useFireflyEditModal();

	const handleSubmit = useCallback(() => {
		if (!artifact.process_id) return;
		trackEvent('Digital Ad Submitted', { artifact_id: artifact.id });
		bridgeApi.sendAction({
			type: 'do_mark_setup_item_completed',
			process_id: artifact.process_id,
			item_id: artifact.id,
		});
		goToNextItem(artifact.process_id, artifact.id);
	}, [bridgeApi, artifact.id, artifact.process_id, goToNextItem]);

	const handleSave = useCallback(() => {
		bridgeApi.sendAction({
			type: 'do_save_artifact',
			artifact_id: artifact.id,
		});
		setSavedInterstitialOpen(true);
	}, [bridgeApi, artifact.id]);

	const aspectRatio = '1 / 1';
	const title = TYPE_TITLES[artifact.type] ?? 'Review Digital Ad';

	const isGenerating = useIsGeneratingImages(artifact);
	const hasGenerationError = !!artifact.content.last_generation_error;

	// A digital ad is always a single square image, so retry regenerates one
	// slide — the carousel slide-count logic in PostArtifactPanel does not apply.
	const handleRetry = useCallback(() => {
		const prompt = artifact.content.image_prompt?.trim();
		bridgeApi.sendAction({
			type: 'do_generate_post_images_for_artifact',
			artifact_id: artifact.id,
			slide_count: 1,
			slide_prompts: prompt ? [prompt] : [],
		});
	}, [bridgeApi, artifact.id, artifact.content.image_prompt]);

	const { openPicker, elements: imageReplaceElements } = useImageReplace({
		artifactId: artifact.id,
	});

	const snapshot = artifact.process_id
		? (bridgeApi.user_state?.process_setup_snapshots?.[artifact.process_id] ??
			null)
		: null;
	const isStandalone = (snapshot?.children.length ?? 0) === 1;
	const showSubmitButton = isStandalone || isArtifactActionable(artifact);
	const actionRenderer =
		showSubmitButton && !savedInterstitialOpen
			? () => (
					<div className="ml-auto flex items-center gap-2">
						<Button
							type="button"
							variant="accent"
							onPress={handleSave}
							isDisabled={isGenerating || hasGenerationError}
						>
							Save
						</Button>
					</div>
				)
			: undefined;

	const imageUrl = artifact.content.image_urls?.[0];

	const isEditingImage = Object.values(
		bridgeApi.user_state?.outstanding_requests ?? {},
	).some(
		(req) =>
			req?.type === 'request_edit_image' && req.artifact_id === artifact.id,
	);

	const showOverlayButtons =
		!isGenerating && !!imageUrl && !isEditingImage && !savedInterstitialOpen;

	return (
		<ChatPanelLayout
			title={savedInterstitialOpen ? 'Saved' : title}
			actionRenderer={actionRenderer}
			onClose={onClose}
			style={style}
		>
			{savedInterstitialOpen ? (
				<SuccessInterstitial
					title="Saved"
					summary="Your ad draft is saved. Boost it manually in Meta Ads Manager when you're ready."
					primaryAction={{
						label: 'Done',
						onPress: () => {
							setSavedInterstitialOpen(false);
							handleSubmit();
						},
					}}
				/>
			) : (
				<div className="flex w-full flex-col">
					<div
						className="[container-type:inline-size] relative w-full
							overflow-x-hidden"
						style={{ height: 'min(82vh, 820px)' }}
						onMouseEnter={() => setIsViewportHovered(true)}
						onMouseLeave={() => setIsViewportHovered(false)}
					>
						<div className="flex h-full items-center justify-center px-4">
							{isGenerating ? (
								<div
									className="flex h-full animate-pulse items-center
										justify-center overflow-hidden rounded-2xl bg-gray-100"
									style={{ aspectRatio }}
								>
									<BridgewayGlyph />
								</div>
							) : hasGenerationError ? (
								// Generation failed: offer recovery (regenerate or upload your
								// own) instead of pinning on the placeholder forever. Checked
								// before the no-image branch so a failure never falls through to
								// the infinite spinner. The upload hook's error overlay is
								// absolute, so this block is the position: relative parent it fills.
								<div
									className="relative flex flex-col items-center gap-3 px-4
										py-8"
								>
									<p className="text-sm text-red-700">
										Couldn&apos;t generate the digital ad.
									</p>
									<div className="flex items-center gap-2">
										<Button variant="secondary" onPress={handleRetry}>
											Retry
										</Button>
										<Button variant="secondary" onPress={openPicker}>
											<Upload />
											<Text>Upload image</Text>
										</Button>
									</div>
									{imageReplaceElements}
								</div>
							) : !imageUrl ? (
								<div
									className="flex h-full animate-pulse items-center
										justify-center overflow-hidden rounded-2xl bg-gray-100"
									style={{ aspectRatio }}
								>
									<BridgewayGlyph />
								</div>
							) : (
								<div
									className="relative flex h-full items-center justify-center"
								>
									<BlobImage
										src={imageUrl}
										alt="Digital ad preview"
										className="block max-h-full max-w-full rounded-2xl
											object-contain"
									/>

									{showOverlayButtons && (
										<div
											data-testid="digital-ad-overlay-buttons"
											className={cn(
												`absolute top-4 left-4 z-10 flex flex-col gap-2
													transition-opacity duration-150`,
												isViewportHovered
													? 'opacity-100'
													: 'pointer-events-none opacity-0',
											)}
										>
											<RailTooltip label="Download">
												<ActionButton
													aria-label="Download ad"
													onPress={handleDownload}
													UNSAFE_className={toolRailButtonClass}
												>
													<Download />
												</ActionButton>
											</RailTooltip>
											{imageUrl && <VibeEditButton />}
											{imageUrl && (
												<RailTooltip label="Edit with Firefly">
													<ActionButton
														aria-label="Edit image"
														onPress={() =>
															fireflyEdit.open(imageUrl, {
																artifactId: artifact.id,
															})
														}
														UNSAFE_className={toolRailButtonClass}
													>
														<FireflyIcon />
													</ActionButton>
												</RailTooltip>
											)}
										</div>
									)}

									{isEditingImage && (
										<div
											data-testid="image-editing-overlay"
											className="pointer-events-none absolute inset-0 z-20"
										>
											<ImageEditingOverlay />
										</div>
									)}
								</div>
							)}
						</div>
					</div>
					{isGenerating && (
						<div className="flex flex-col items-center gap-1 px-4 py-3">
							<span className="text-sm text-gray-700">Generating…</span>
						</div>
					)}
				</div>
			)}
			{fireflyEdit.modal}
		</ChatPanelLayout>
	);
};
